import { Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface PaginationProps {
  currentPage: number
  totalPages: number
  prevPage: number | null
  nextPage: number | null
  baseUrl?: string
  queryParams?: Record<string, string>
}

export function Pagination({
  currentPage,
  totalPages,
  prevPage,
  nextPage,
  baseUrl,
  queryParams,
}: PaginationProps) {
  const effectiveBaseUrl = baseUrl ?? "/phrases"
  const effectiveQueryParams = queryParams ?? {}
  if (totalPages <= 1) return null

  // Generate URL with params
  const getUrl = (page: number) => {
    const params = new URLSearchParams()
    if (page > 1) params.set("page", page.toString())

    // Add other existing params (like search)
    Object.entries(effectiveQueryParams).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })

    const queryString = params.toString()
    return `${effectiveBaseUrl}${queryString ? `?${queryString}` : ""}`
  }

  // Generate page numbers to show
  const getPageNumbers = () => {
    const delta = 2 // Current page +/- delta
    const range = []
    const rangeWithDots = []
    let l

    for (let i = 1; i <= totalPages; i++) {
      // Always show first, last, and pages around current
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i)
      }
    }

    // Add dots
    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1)
        } else if (i - l !== 1) {
          rangeWithDots.push("...")
        }
      }
      rangeWithDots.push(i)
      l = i
    }

    return rangeWithDots
  }

  return (
    <div className="flex items-center justify-center gap-1 py-4">
      {/* First Page */}
      <Button
        variant="outline"
        size="icon"
        className="hidden size-9 sm:flex"
        disabled={currentPage === 1}
        asChild={currentPage !== 1}
      >
        {currentPage === 1 ? (
          <ChevronsLeft className="size-4" />
        ) : (
          <Link href={getUrl(1)} preserveScroll>
            <ChevronsLeft className="size-4" />
            <span className="sr-only">First page</span>
          </Link>
        )}
      </Button>

      {/* Prev Page */}
      <Button
        variant="outline"
        size="icon"
        className="size-9"
        disabled={!prevPage}
        asChild={!!prevPage}
      >
        {!prevPage ? (
          <ChevronLeft className="size-4" />
        ) : (
          <Link href={getUrl(prevPage)} preserveScroll rel="prev">
            <ChevronLeft className="size-4" />
            <span className="sr-only">Previous page</span>
          </Link>
        )}
      </Button>

      {/* Numbered Pages */}
      <div className="flex items-center gap-1">
        {getPageNumbers().map((page, i) => (
          page === "..." ? (
            <span key={`dots-${i}`} className="flex size-9 items-center justify-center text-sm text-muted-foreground">
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="icon"
              className={cn("size-9", page === currentPage && "pointer-events-none")}
              asChild={page !== currentPage}
            >
              {page === currentPage ? (
                <span aria-label={`Page ${page}`}>{page}</span>
              ) : (
                <Link href={getUrl(page as number)} preserveScroll aria-label={`Page ${page}`}>
                  {page}
                </Link>
              )}
            </Button>
          )
        ))}
      </div>

      {/* Next Page */}
      <Button
        variant="outline"
        size="icon"
        className="size-9"
        disabled={!nextPage}
        asChild={!!nextPage}
      >
        {!nextPage ? (
          <ChevronRight className="size-4" />
        ) : (
          <Link href={getUrl(nextPage)} preserveScroll rel="next">
            <ChevronRight className="size-4" />
            <span className="sr-only">Next page</span>
          </Link>
        )}
      </Button>

      {/* Last Page */}
      <Button
        variant="outline"
        size="icon"
        className="hidden size-9 sm:flex"
        disabled={currentPage === totalPages}
        asChild={currentPage !== totalPages}
      >
        {currentPage === totalPages ? (
          <ChevronsRight className="size-4" />
        ) : (
          <Link href={getUrl(totalPages)} preserveScroll>
            <ChevronsRight className="size-4" />
            <span className="sr-only">Last page</span>
          </Link>
        )}
      </Button>
    </div>
  )
}
